import React, { useState, useEffect, useMemo } from 'react';
import { API_BASE_URL } from '../config';
import SortableTable from '../components/SortableTable';
import { safeFormatDate, safeFormatUTCDateTime } from '../utils/dateUtils';
import DateRangeSelector from '../components/DateRangeSelector';
import GIFollowupModal from '../components/GI/GIFollowupModal';
import GIStatsModal from '../components/GI/GIStatsModal';
import Loading from '../components/Loading';
import useAnalytics from '../hooks/useAnalytics';

const GIUnansweredSurveys = ({ fetchWithAuth, currentUser }) => {
  useAnalytics({ currentUser, module: 'GI_UNANSWERED_SURVEYS' });

  const [startDate, setStartDate] = useState(() => {
    // Default to first day of current month
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const [endDate, setEndDate] = useState(() => {
    // Default to last day of current month
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  });

  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [followups, setFollowups] = useState([]);

  const activeSurveys = useMemo(() => {
    return surveys.filter(({ CampaignEnabled }) => CampaignEnabled);
  }, [surveys]);

  // État pour la modale des followups
  const [isFollowupModalOpen, setIsFollowupModalOpen] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);

  // État pour la modale des statistiques
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);

  // Fonction utilitaire pour créer le mapping des followups
  const createFollowupMapping = (followupsData) => {
    const followupMapped = {};

    followupsData.followups.forEach(followup => {
      let key = `${followup.missiondayid}.${followup.promoterid}`;

      // small hack for 200ish first answers... could be removed later
      if (!followup.promoterid)
        key = followup.missiondayid;

      if (!followupMapped[key])
        followupMapped[key] = [];

      followupMapped[key].push(followup);
    });
    return followupMapped;
  };

  // Fonction utilitaire pour enrichir les surveys avec les données de followups
  const enrichSurveysWithFollowups = (surveys, followupMapped) => {

    return surveys.map(survey => {
      let key = `${survey.MissionDayId}.${survey.PromoterId}`;

      // small hack for 200ish first answers... could be removed later
      if (followupMapped[survey.MissionDayId]) {
        key = survey.MissionDayId;
      }

      const surveyFollowups = followupMapped[key] || [];

      // Trier par date de création décroissante pour avoir le plus récent en premier
      const sortedFollowups = surveyFollowups.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const lastFollowup = sortedFollowups[0];

      return {
        ...survey,
        FollowupCount: surveyFollowups.length,
        LastFollowupBy: lastFollowup?.username || null,
        LastFollowupDate: lastFollowup?.created_at || null,
        LastFollowupStatus: lastFollowup?.status || null
      };
    });
  };

    // Fonction pour fetch les followups uniquement
  const fetchFollowups = async (silent = false) => {
    if (!startDate || !endDate) return;

    if (!silent) {
      setError(null);
    }

    try {
      const formattedStartDate = startDate.toISOString().split('T')[0];
      const formattedEndDate = endDate.toISOString().split('T')[0];

      const followupsResponse = await fetchWithAuth(
        `${API_BASE_URL}/api/dwh/gi/followups?startDate=${formattedStartDate}&endDate=${formattedEndDate}`
      );

      if (followupsResponse.ok) {
        const followupsData = await followupsResponse.json();
        const followupMapped = createFollowupMapping(followupsData);

        setFollowups(followupMapped);

        // Mettre à jour les surveys avec les nouvelles données de followups
        setSurveys(prevSurveys => enrichSurveysWithFollowups(prevSurveys, followupMapped));
      } else if (!silent) {
        setError('Erreur lors du chargement des followups');
        console.error("Failed to fetch followups");
      }
    } catch (error) {
      if (!silent) {
        setError('Erreur lors du chargement des followups');
        console.error("Error fetching followups:", error);
      }
    }
  };

  // Fetch des surveys non répondus et des followups
  useEffect(() => {
    const fetchData = async () => {
      if (!startDate || !endDate) return;

      setLoading(true);
      setError(null);

      try {
        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];

        // Appel parallèle des deux APIs
        const [surveysResponse, followupsResponse] = await Promise.all([
          fetchWithAuth(
            `${API_BASE_URL}/api/dwh/gi/unanswered-surveys?startDate=${formattedStartDate}&endDate=${formattedEndDate}`
          ),
          fetchWithAuth(
            `${API_BASE_URL}/api/dwh/gi/followups?startDate=${formattedStartDate}&endDate=${formattedEndDate}`
          )
        ]);

        if (surveysResponse.ok && followupsResponse.ok) {
          const surveysData = await surveysResponse.json();
          const followupsData = await followupsResponse.json();

          const followupMapped = createFollowupMapping(followupsData);
          const enrichedSurveys = enrichSurveysWithFollowups(surveysData.data || [], followupMapped);

          setSurveys(enrichedSurveys);
          setFollowups(followupMapped);
        } else {
          setError('Erreur lors du chargement des données');
          console.error("Failed to fetch data");
        }
      } catch (error) {
        setError('Erreur lors du chargement des données');
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, fetchWithAuth]);

  // Polling des followups toutes les minutes
  useEffect(() => {
    if (!startDate || !endDate) return;

    const interval = setInterval(() => {
      fetchFollowups(true); // silent = true pour ne pas afficher les erreurs lors du polling
    }, 60000); // 60 secondes

    return () => clearInterval(interval);
  }, [startDate, endDate, fetchWithAuth]);

  // Handler pour ouvrir la modale de followup
  const handleRowClick = (survey) => {
    setSelectedSurvey(survey);
    setIsFollowupModalOpen(true);
  };

  // Handler pour fermer la modale de followup
  const handleCloseFollowupModal = () => {
    setIsFollowupModalOpen(false);
    setSelectedSurvey(null);
  };

  // Handler pour ouvrir/fermer la modale de statistiques
  const handleOpenStatsModal = () => {
    setIsStatsModalOpen(true);
  };

  const handleCloseStatsModal = () => {
    setIsStatsModalOpen(false);
  };

  // Handler appelé quand un followup est ajouté ou supprimé
  const handleFollowupAdded = (missionDayId, increment = 1, status = 'pending') => {
    // Mettre à jour le count des followups dans la liste
    setSurveys(prevSurveys =>
      prevSurveys.map(survey => {
        if (survey.MissionDayId === missionDayId) {
          const newCount = Math.max(0, (survey.FollowupCount || 0) + increment);

          // Si on supprime et que c'était la dernière relance, réinitialiser les infos
          if (increment < 0 && newCount === 0) {
            return {
              ...survey,
              FollowupCount: 0,
              LastFollowupBy: null,
              LastFollowupDate: null,
              LastFollowupStatus: null
            };
          }

          // Si on ajoute une relance, mettre à jour avec les infos actuelles
          if (increment > 0) {
            return {
              ...survey,
              FollowupCount: newCount,
              LastFollowupBy: currentUser.username,
              LastFollowupDate: new Date().toISOString(),
              LastFollowupStatus: status
            };
          }

          // Pour les suppressions (mais pas la dernière), juste mettre à jour le count
          return {
            ...survey,
            FollowupCount: newCount
          };
        }
        return survey;
      })
    );
  };

  // Définir les colonnes pour le tableau
  const columns = [
    {
      key: 'MissionDayId',
      label: '#Id',
      render: (survey) => `${survey.MissionDayId}.${survey.PromoterId}`,
      rawValue: (survey) => `${survey.MissionDayId}.${survey.PromoterId}`,
    },
    {
      key: 'MissionDayDate',
      label: 'Date mission',
      render: (survey) => safeFormatDate(survey.MissionDayDate),
      rawValue: (survey) => survey.MissionDayDate
    },
    {
      key: '_retard',
      label: 'Retard',
      rawValue: (survey) => {
        const today = new Date();
        const missionDayDate = new Date(survey.MissionDayDate);
        const delay = today.getTime() - missionDayDate.getTime();
        return Math.floor(delay / (1000 * 60 * 60 * 24));
      },
      render: (survey) => {
          const today = new Date();
          const missionDayDate = new Date(survey.MissionDayDate);
          const delay = today.getTime() - missionDayDate.getTime();
          return `${Math.floor(delay / (1000 * 60 * 60 * 24))} jour${Math.floor(delay / (1000 * 60 * 60 * 24)) > 1 ? 's' : ''}`;
      }
    },
    {
      key: 'LastFollowupStatus',
      label: 'Status',
      render: (survey) => (
        <div className="text-center">
          {survey.LastFollowupStatus ? (
            getStatusDisplay(survey.LastFollowupStatus)
          ) : (
            <span className="text-gray-400 dark:text-gray-500 text-sm italic">
              Jamais relancé
            </span>
          )}
        </div>
      ),
      rawValue: (survey) => {
        if (!survey.LastFollowupStatus) return 'Jamais relancé';
        const statusLabels = {
          pending: 'En attente',
          done: 'Terminé',
          wontdo: 'Abandonné'
        };
        return statusLabels[survey.LastFollowupStatus] || survey.LastFollowupStatus;
      }
    },
    {
      key: 'FollowupCount',
      label: '# Relances',
      render: (survey) => (
        <div className="flex items-center justify-center">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            survey.FollowupCount > 0
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
          }`}>
            {survey.FollowupCount || 0}
          </span>
        </div>
      ),
      rawValue: (survey) => survey.FollowupCount || 0
    },
    {
      key: 'LastFollowupBy',
      label: 'Dernière relance par',
      render: (survey) => (
        <div className="text-center">
          {survey.LastFollowupBy ? (
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {survey.LastFollowupBy}
            </div>
          ) : (
            <span className="text-gray-400 dark:text-gray-500 text-sm italic">
              Aucune relance
            </span>
          )}
        </div>
      ),
      rawValue: (survey) => survey.LastFollowupBy || ''
    },
    {
      key: 'LastFollowupDate',
      label: 'Dernière date de relance',
      render: (survey) => (
        <div className="text-center">
          {survey.LastFollowupDate ? (
            <div className="text-sm text-gray-900 dark:text-gray-100">
              {safeFormatUTCDateTime(survey.LastFollowupDate, 'fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          ) : (
            <span className="text-gray-400 dark:text-gray-500 text-sm italic">
              Jamais relancé
            </span>
          )}
        </div>
      ),
      rawValue: (survey) => survey.LastFollowupDate ? safeFormatUTCDateTime(survey.LastFollowupDate, 'fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }) : ''
    },
    {
      key: 'promoter_name',
      label: 'Promoteur',
      render: (survey) => {
        let phone = survey.PromoterPhone;

        if (phone && phone.startsWith('33')) {
          phone = phone.replace('33', '+33');
        }

        return <div>
          <div>{`${survey.PromoterFirstName || ''} ${survey.PromoterLastName || ''}`.trim()}</div>
          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <span>{phone || '-'}</span>
            {survey.PromoterPhone && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    alert('Numéro copié dans le presse-papiers');
                    navigator.clipboard.writeText(phone);
                  }}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                  title="Copier le numéro"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
                <a
                  href={`tel:${phone}`}
                  onClick={(e) => e.stopPropagation()}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                  title="Appeler"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </a>
              </>
            )}
          </div>
        </div>
      },
      rawValue: (survey) => `${survey.PromoterFirstName || ''} ${survey.PromoterLastName || ''}`.trim()
    },
    {
      key: 'CampaignName',
      label: 'Campagne',
      render: (survey) => (
        <div>
          <div>{survey.CampaignName || '-'}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {survey.BrandDisplayName || survey.BrandName || '-'}
          </div>
        </div>
      )
    },
    {
      key: 'client_name',
      label: 'Client',
      rawValue: (survey) => survey.ClientName || survey.CampaignClientName || '-',
      render: (survey) => survey.ClientName || survey.CampaignClientName || '-'
    },
    {
      key: 'InterventionPointName',
      label: 'Point d\'intervention',
      render: (survey) => {
        let phone = survey.InterventionPointPhone;

        if (phone && phone.startsWith('33')) {
          phone = phone.replace('33', '+33');
        }

        return (
          <div>
            <div className="font-medium">{survey.InterventionPointName || '-'}</div>
            {survey.InterventionPointPhone && (
              <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <span>{phone || '-'}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    alert('Numéro copié dans le presse-papiers');
                    navigator.clipboard.writeText(phone);
                  }}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                  title="Copier le numéro"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
                <a
                  href={`tel:${phone}`}
                  onClick={(e) => e.stopPropagation()}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                  title="Appeler"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </a>
              </div>
            )}
            {/* {(survey.InterventionPointCity || survey.InterventionPointZip) && (
              <div className="text-xs text-gray-400 dark:text-gray-500">
                {[survey.InterventionPointZip, survey.InterventionPointCity].filter(Boolean).join(' ')}
              </div>
            )} */}
          </div>
        );
      },
      rawValue: (survey) => survey.InterventionPointName || '-'
    }
  ];

  const handleDateRangeChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  // Fonction pour afficher le statut
  const getStatusDisplay = (status) => {
    if (!status) return null;

    const statusConfig = {
      pending: {
        label: 'En attente',
        icon: '⏳',
        bgColor: 'bg-yellow-100 dark:bg-yellow-900/20',
        textColor: 'text-yellow-800 dark:text-yellow-200',
        borderColor: 'border-yellow-200 dark:border-yellow-800'
      },
      done: {
        label: 'Rempli',
        icon: '✅',
        bgColor: 'bg-green-100 dark:bg-green-900/20',
        textColor: 'text-green-800 dark:text-green-200',
        borderColor: 'border-green-200 dark:border-green-800'
      },
      wontdo: {
        label: 'Abandonné',
        icon: '❌',
        bgColor: 'bg-red-100 dark:bg-red-900/20',
        textColor: 'text-red-800 dark:text-red-200',
        borderColor: 'border-red-200 dark:border-red-800'
      }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${config.bgColor} ${config.textColor} ${config.borderColor}`}>
        <span className="mr-1">{config.icon}</span>
        {config.label}
      </span>
    );
  };

  return (
    <div className="container mx-auto p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          Relance Formulaires GI non remplis
        </h1>

        <div className="mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 w-full">
            <div className="flex items-center gap-4">
              <button
                onClick={handleOpenStatsModal}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Voir les statistiques
              </button>
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="text-gray-700 dark:text-gray-300 font-medium">
                Sélectionnez une période :
              </div>
              <div className="w-[300px]">
                <DateRangeSelector
                  startDate={startDate}
                  endDate={endDate}
                  onDatesChange={handleDateRangeChange}
                  maxDateRange={{ value: 12, unit: 'months' }}
                  relative={false}
                />
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="my-8">
            <Loading
              size="lg"
              message="Chargement des formulaires non remplis..."
              showFunFact={true}
              className="py-8"
            />
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 my-4">
            <p className="text-red-700 dark:text-red-300">{error}</p>
          </div>
        ) : (
          <div>
            <div className="flex justify-between items-center mb-4">
              <div className="text-gray-700 dark:text-gray-300">
                Période: <span className="font-medium">{safeFormatDate(startDate)} - {safeFormatDate(endDate)}</span>
              </div>
              <div className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 py-1 px-3 rounded-full text-sm">
                {activeSurveys.length} formulaire{activeSurveys.length > 1 ? 's' : ''} non rempli{activeSurveys.length > 1 ? 's' : ''}
              </div>
            </div>
            <div className="h-[calc(100vh-280px)]">
              <SortableTable
                data={activeSurveys}
                columns={columns}
                emptyMessage="Aucun formulaire non rempli trouvé pour cette période"
                exportable={true}
                exportFilename="formulaires-non-remplis.csv"
                initialSortBy={{ key: '_retard', direction: 'descending' }}
                onRowClick={handleRowClick}
                className="h-[calc(100vh-280px)] flex flex-col h-full relative"
                globalSearch={true}
              />
            </div>
          </div>
        )}
      </div>

      {/* Modale des followups */}
      <GIFollowupModal
        isOpen={isFollowupModalOpen}
        onClose={handleCloseFollowupModal}
        survey={selectedSurvey}
        fetchWithAuth={fetchWithAuth}
        currentUser={currentUser}
        onFollowupAdded={handleFollowupAdded}
      />

      {/* Modale des statistiques */}
      <GIStatsModal
        isOpen={isStatsModalOpen}
        onClose={handleCloseStatsModal}
        startDate={startDate}
        endDate={endDate}
        fetchWithAuth={fetchWithAuth}
        surveys={surveys}
        followups={followups}
      />
    </div>
  );
};

export default GIUnansweredSurveys;